import {
  useFetchOnboardingStatus,
  useOnboardingQnA,
  useTourProgress,
} from "@/hooks/use-organisation";
import { OnboardingQnAData } from "@/types/organisation.types";
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

interface OnboardingContextType {
  isSaving: boolean;
  open: boolean;
  setOpen: (open: boolean) => void;
  currentStep: number;
  totalSteps: number;
  onboardingData: OnboardingQnAData;
  updateOnboardingData: (data: Partial<OnboardingQnAData>) => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (step: number) => void;
  isStepComplete: (step: number) => boolean;
  handleOnFinish: (type: "complete" | "close") => void;
}

const defaultOnboardingData: OnboardingQnAData = {
  target_audience: "",
  employee_count: "",
  industry: "",
  previous_warmup_usage: "",
  mailbox_count: "",
  monthly_email_volume: "",
  referral_source: "",
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined
);

// const isOnboardingWizardPending = (orgId: string): boolean => {
//   const key = `onboarding-wizard-completed-${orgId}`;
//   const localState = localStorage.getItem(key);

//   if (localState === "true") return false;
//   if (localState === "false") return true;

//   // Fallback for invalid, corrupted, or missing values
//   return true;
// };

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [open, setOpen] = useState(false);
  const { refetch: refetchTourProgress } = useTourProgress();
  const [currentStep, setCurrentStep] = useState(1);
  const [onboardingData, setOnboardingData] = useState<OnboardingQnAData>(
    defaultOnboardingData
  );

  const { data: onboardingStatus, isLoading: isOnboardingStatusLoading } =
    useFetchOnboardingStatus();

  const { mutateAsync: onboardingQnA, isPending: isOnboardingQnAPending } =
    useOnboardingQnA();

  const totalSteps = 7;

  const updateOnboardingData = (data: Partial<OnboardingQnAData>) => {
    setOnboardingData((prev) => ({ ...prev, ...data }));
  };

  const handleOnFinish = async (type: "complete" | "close") => {
    try {
      await onboardingQnA(onboardingData);

      window.cioanalytics?.track?.(`onboarding_${type}`, {
        onboarding_data: onboardingData,
      });
    } catch (error) {
      console.error(error);
    } finally {
      setOpen(false);
      await refetchTourProgress();
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      // check if the current step is complete
      if (!isStepComplete(currentStep)) {
        return;
      }

      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const goToStep = (step: number) => {
    if (step >= 1 && step <= totalSteps) {
      setCurrentStep(step);
    }
  };

  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!onboardingData.target_audience;
      case 2:
        return !!onboardingData.employee_count;
      case 3:
        return !!onboardingData.industry;
      case 4:
        return !!onboardingData.previous_warmup_usage;
      case 5:
        return !!onboardingData.mailbox_count;
      case 6:
        return !!onboardingData.monthly_email_volume;
      case 7:
        return !!onboardingData.referral_source;

      default:
        return false;
    }
  };

  useEffect(() => {
    if (
      !isOnboardingStatusLoading &&
      !onboardingStatus?.isOnboardingCompleted
    ) {
      setOpen(true);
    }
  }, [isOnboardingStatusLoading, onboardingStatus]);

  return (
    <OnboardingContext.Provider
      value={{
        isSaving: isOnboardingQnAPending,
        open,
        setOpen,
        currentStep,
        totalSteps,
        onboardingData,
        updateOnboardingData,
        nextStep,
        prevStep,
        goToStep,
        isStepComplete,
        handleOnFinish,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};
